<template>
  <v-card
    outlined
    class="queue-run-card"
    :class="`queue-run-card--${statusKey.toLowerCase()}`"
    :data-run-id="runId"
  >
    <v-progress-linear
      v-if="pending"
      indeterminate
      height="2"
      color="primary"
      class="queue-run-card__pending"
    />

    <div class="queue-run-card__body">
      <div class="queue-run-card__head">
        <v-icon small class="mr-1 queue-run-card__source-icon" :title="source.label">{{
          source.icon
        }}</v-icon>
        <span
          v-if="queuePosition != null && lane === 'waiting'"
          class="queue-run-card__position"
          :title="run.position != null ? `position ${run.position}` : undefined"
          >#{{ queuePosition }}</span
        >
        <a
          v-if="openable"
          href="#"
          class="queue-run-card__title"
          @click.prevent="$emit('open', run)"
        >
          {{ title }}
        </a>
        <span v-else class="queue-run-card__title">{{ title }}</span>
        <v-spacer />
        <v-chip
          small
          :color="status.color"
          :dark="status.dark"
          class="queue-run-card__status"
          :data-status="statusKey"
        >
          <v-progress-circular
            v-if="status.spinner"
            indeterminate
            size="12"
            width="2"
            class="mr-1"
          />
          {{ status.label }}
        </v-chip>
      </div>

      <div class="queue-run-card__meta caption grey--text text--darken-1">
        <span v-if="run.attempt != null">Run {{ run.attempt }}</span>
        <span v-if="run.batch">Batch {{ run.batch }}</span>
        <span v-if="run.requested_by != null">requested by user {{ run.requested_by }}</span>
        <span v-if="timeline">{{ timeline }}</span>
        <span v-if="run.worker_id">{{ run.worker_id }}</span>
      </div>

      <div v-if="lane === 'running'" class="queue-run-card__progress">
        <div class="caption d-flex align-center">
          <span class="queue-run-card__step">{{ run.current_step || 'starting' }}</span>
          <v-spacer />
          <span>{{ progressPct }} %</span>
        </div>
        <v-progress-linear
          :value="progressPct"
          height="6"
          rounded
          :color="status.color"
          :indeterminate="progressPct === 0"
        />
        <div
          class="caption grey--text queue-run-card__heartbeat"
          :class="{ 'error--text': actions.heartbeatStale }"
        >
          <v-icon x-small :color="actions.heartbeatStale ? 'error' : 'grey'" class="mr-1"
            >mdi-heart-pulse</v-icon
          >
          <span v-if="heartbeatAge">heartbeat {{ heartbeatAge }}</span>
          <span v-else>no heartbeat yet</span>
          <span v-if="actions.heartbeatStale"> · worker may be lost</span>
        </div>
      </div>

      <div v-if="lane === 'recent' && run.error" class="caption error--text queue-run-card__error">
        {{ run.error.type }}<span v-if="run.error.stage"> @ {{ run.error.stage }}</span
        >: {{ run.error.message }}
      </div>

      <div v-if="chips.length || degraded || enginesRequested.length" class="queue-run-card__chips">
        <template v-if="chips.length">
          <template v-for="chip in chips">
            <v-tooltip v-if="chip.tooltip" :key="chip.key" bottom>
              <template #activator="{ on }">
                <v-chip
                  x-small
                  outlined
                  class="mr-1 mb-1 queue-run-card__engine"
                  :data-engine="chip.key"
                  v-on="on"
                >
                  {{ chip.text }}
                </v-chip>
              </template>
              <span>{{ chip.tooltip }}</span>
            </v-tooltip>
            <v-chip
              v-else
              :key="chip.key"
              x-small
              outlined
              class="mr-1 mb-1 queue-run-card__engine"
              :class="{
                'queue-run-card__engine--mono': chip.mono,
                'queue-run-card__engine--dev': chip.dev,
              }"
              :data-engine="chip.key"
            >
              {{ chip.text }}
            </v-chip>
          </template>
        </template>
        <template v-else>
          <v-chip
            v-for="name in enginesRequested"
            :key="`req-${name}`"
            x-small
            outlined
            class="mr-1 mb-1 queue-run-card__engine queue-run-card__engine--requested"
            :data-engine="name"
          >
            {{ name }}
          </v-chip>
        </template>
        <v-chip
          v-if="degraded"
          x-small
          outlined
          color="warning"
          class="mr-1 mb-1 queue-run-card__degraded"
          >Degraded</v-chip
        >
      </div>
    </div>

    <v-card-actions v-if="hasActions" class="queue-run-card__actions">
      <v-spacer />
      <v-btn
        v-if="actions.remove"
        small
        text
        color="error"
        class="queue-run-card__action queue-run-card__action--remove"
        :disabled="pending"
        @click="$emit('remove', run)"
      >
        <v-icon small left>mdi-close</v-icon>
        Delete
      </v-btn>
      <v-btn
        v-if="actions.cancel"
        small
        text
        color="warning"
        class="queue-run-card__action queue-run-card__action--cancel"
        :disabled="pending"
        @click="$emit('cancel', run)"
      >
        <v-icon small left>mdi-cancel</v-icon>
        Cancel
      </v-btn>
      <v-btn
        v-if="actions.force"
        small
        text
        color="error"
        class="queue-run-card__action queue-run-card__action--force"
        :disabled="pending"
        @click="$emit('force', run)"
      >
        <v-icon small left>mdi-flash-alert</v-icon>
        Force cancel
      </v-btn>
      <v-btn
        v-if="actions.markFailed"
        small
        text
        color="error"
        class="queue-run-card__action queue-run-card__action--mark-failed"
        :disabled="pending"
        @click="$emit('mark-failed', run)"
      >
        <v-icon small left>mdi-alert-octagon-outline</v-icon>
        Mark as failed
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  availableActions,
  engineBlockOf,
  engineChips,
  formatAge,
  formatDuration,
  lastSeenAt,
  sourceLabel,
  sourcePresentation,
  sourceRoute,
  statusPresentation,
} from './queueFormat';
import { laneForStatus, normalizeRunStatus, runIdOf } from '@/store/modules/analysisQueue';

/**
 * One run in a queue lane: source, status, progress, engine stamp and the control actions that
 * apply to its current status (Delete / Cancel / Force cancel / Mark as failed).
 */
export default {
  name: 'QueueRunCard',

  props: {
    /** Ledger run document (RunDTO). */
    run: {
      type: Object,
      required: true,
    },
    /** Reference instant for ages, refreshed by the page so cards re-render without a timer each. */
    now: {
      type: Number,
      default: () => Date.now(),
    },
    /** True while a control request for this run is in flight. */
    pending: {
      type: Boolean,
      default: false,
    },
    /** Server flag mirror: when false the Force button is hidden even for CANCEL_REQUESTED runs. */
    forceEnabled: {
      type: Boolean,
      default: true,
    },
    heartbeatTtlS: {
      type: Number,
      default: undefined,
    },
    cancelGraceS: {
      type: Number,
      default: undefined,
    },
    /** 1-based place in the waiting lane (falls back to run.queue_index, then run.position). */
    queueIndex: {
      type: Number,
      default: null,
    },
  },

  computed: {
    runId() {
      return runIdOf(this.run);
    },
    statusKey() {
      return normalizeRunStatus(this.run.status) || 'UNKNOWN';
    },
    lane() {
      return laneForStatus(this.run.status);
    },
    queuePosition() {
      if (this.queueIndex != null) return this.queueIndex;
      if (this.run.queue_index != null) return this.run.queue_index;
      return this.run.position != null ? this.run.position : null;
    },
    status() {
      return statusPresentation(this.run.status);
    },
    source() {
      return sourcePresentation(this.run.source_type);
    },
    title() {
      return sourceLabel(this.run);
    },
    openable() {
      return Boolean(sourceRoute(this.run));
    },
    progressPct() {
      const value = Number(this.run.progress);
      if (!Number.isFinite(value)) return 0;
      return Math.min(100, Math.max(0, Math.round(value)));
    },
    heartbeatAge() {
      return formatAge(lastSeenAt(this.run), this.now);
    },
    timeline() {
      const run = this.run;
      if (this.lane === 'waiting') {
        return run.enqueued_at ? `queued ${formatAge(run.enqueued_at, this.now)}` : '';
      }
      if (this.lane === 'running') {
        return run.started_at
          ? `started ${formatAge(run.started_at, this.now)}`
          : 'not started yet';
      }
      const parts = [];
      if (run.started_at && run.finished_at)
        parts.push(formatDuration(run.started_at, run.finished_at));
      if (run.finished_at) parts.push(`finished ${formatAge(run.finished_at, this.now)}`);
      return parts.join(' · ');
    },
    chips() {
      return engineChips(this.run);
    },
    enginesRequested() {
      return Array.isArray(this.run.engines_requested) ? this.run.engines_requested : [];
    },
    degraded() {
      return Boolean(engineBlockOf(this.run)?.degraded);
    },
    actions() {
      return availableActions(this.run, {
        now: this.now,
        forceEnabled: this.forceEnabled,
        heartbeatTtlS: this.heartbeatTtlS,
        cancelGraceS: this.cancelGraceS,
      });
    },
    hasActions() {
      const a = this.actions;
      return a.remove || a.cancel || a.force || a.markFailed;
    },
  },
};
</script>

<style scoped>
.queue-run-card {
  position: relative;
  margin-bottom: 8px;
}
.queue-run-card__pending {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}
.queue-run-card__body {
  padding: 10px 12px 4px;
}
.queue-run-card__head {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.queue-run-card__position {
  font-weight: 600;
  margin-right: 4px;
  color: rgba(0, 0, 0, 0.6);
}
.queue-run-card__title {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: none;
  color: inherit;
}
a.queue-run-card__title:hover {
  text-decoration: underline;
}
.queue-run-card__status {
  flex-shrink: 0;
}
.queue-run-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0 10px;
  margin-top: 2px;
}
.queue-run-card__progress {
  margin-top: 6px;
}
.queue-run-card__step {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
}
.queue-run-card__heartbeat {
  margin-top: 2px;
  display: flex;
  align-items: center;
}
.queue-run-card__error {
  margin-top: 4px;
  word-break: break-word;
}
.queue-run-card__chips {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.queue-run-card__engine--mono {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
}
.queue-run-card__engine--dev {
  border-color: var(--v-warning-base, #fb8c00) !important;
  color: var(--v-warning-base, #fb8c00) !important;
}
.queue-run-card__actions {
  padding-top: 0;
}
</style>
